import pMap from 'p-map'
import type {FetchFn} from '@wix/document-manager-extensions'
import {ReportableError} from '@wix/document-manager-utils'
export type PageLoadedCallback = (pageJson: any, pageId: string) => void
import type {FragmentListItem} from '@wix/document-services-types/src/documentServicesModel'
interface FragmentResult {
    type: string
    name: string
    content: any
}

export interface FetchFragmentsFacade {
    fetchFragments(
        fetchFn: FetchFn,
        fragmentsList: FragmentListItem[],
        urlTemplates: string[],
        concurrency?: number
    ): Promise<FragmentResult[]>
}

export class DefaultFetchFragmentsFacade implements FetchFragmentsFacade {
    async fetchFragments(
        fetchFn: FetchFn,
        fragmentsList: FragmentListItem[],
        urlTemplates: string[],
        concurrency: number = 100
    ): Promise<FragmentResult[]> {
        return fetchFragments(fetchFn, fragmentsList, urlTemplates, concurrency)
    }
}

async function fetchFragments(
    fetchFn: FetchFn,
    fragmentsList: FragmentListItem[],
    urlTemplates: string[],
    concurrency: number = 100
): Promise<FragmentResult[]> {
    const entries = await pMap(
        fragmentsList,
        ({jsonFilename, fragmentType, fragmentName}) =>
            fetchFragmentJson(fetchFn, urlTemplates, fragmentType, fragmentName, jsonFilename),
        {concurrency}
    )

    return entries
}

async function fetchFragmentJson(
    fetchFn: FetchFn,
    urlTemplates: string[],
    fragmentType: string,
    fragmentName: string,
    jsonFilename: string
): Promise<FragmentResult> {
    for (const urlTemplate of urlTemplates) {
        const url = urlTemplate.replace('{filename}', jsonFilename)
        try {
            const response = await fetchFn(url)
            if (!response) {
                console.warn(`HTTP: failed to fetch fragment ${url}`)
                continue
            }
            if (!response.ok) {
                console.warn(`HTTP: failed to fetch fragment ${url} - ${response.status}: ${response.statusText}`)
                continue
            }
            const fragmentData = await response.json()
            return {type: fragmentType, name: fragmentName, content: fragmentData}
        } catch (e) {
            console.error(`HTTP: failed to fetch fragment ${url}`, e)
        }
    }

    throw new ReportableError({
        message: `Failed to fetch fragment using all fallback URLs`,
        errorType: 'failedToFetchFragments',
        extras: {fragmentType, fragmentName, jsonFilename}
    })
}

export {fetchFragments}
